import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './EmailPage.css';

const API_BASE = '/api';

function EmailPage() {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailContent, setEmailContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mailbox, setMailbox] = useState('');

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE + '/emails');
      if (res.status === 403) {
        setError('Access denied');
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setEmails(data.emails);
        setMailbox(data.mailbox || '');
      }
    } catch (err) {
      setError('Failed to load emails');
    }
    setLoading(false);
  };

  const viewEmail = async (emailId) => {
    try {
      const res = await fetch(API_BASE + '/emails/' + encodeURIComponent(emailId));
      const data = await res.json();
      if (data.success) {
        setSelectedEmail(emailId);
        setEmailContent(data.email);
      }
    } catch (err) {
      console.error('Error fetching email:', err);
    }
  };

  const deleteEmail = async (emailId) => {
    if (!window.confirm('Delete this email?')) return;
    try {
      const res = await fetch(API_BASE + '/emails/' + encodeURIComponent(emailId), {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        setEmails(emails.filter(e => e.id !== emailId));
        if (selectedEmail === emailId) {
          setSelectedEmail(null);
          setEmailContent(null);
        }
      }
    } catch (err) {
      console.error('Error deleting email:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatFrom = (from) => {
    if (!from) return '';
    const match = from.match(/^([^<]+)/);
    return match ? match[1].trim().replace(/"/g, '') : from;
  };

  if (error) {
    return (
      <div className="email-page">
        <div className="email-error">
          <h2>{error}</h2>
          <p>This page is restricted.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Email Inbox</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="email-page">
        <div className="email-page-header">
          <div>
            <h1>Email Inbox</h1>
            <p className="email-mailbox">{mailbox}</p>
          </div>
          <button onClick={fetchEmails} className="email-refresh-btn" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        <div className="email-layout">
          <div className="email-list-panel">
            <div className="email-count">{emails.length} messages</div>
            <table className="email-table">
              <thead>
                <tr>
                  <th>From</th>
                  <th>Subject</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {emails.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="email-empty">
                      {loading ? 'Loading...' : 'No emails'}
                    </td>
                  </tr>
                ) : (
                  emails.map(email => (
                    <tr
                      key={email.id}
                      className={selectedEmail === email.id ? 'selected' : ''}
                      onClick={() => viewEmail(email.id)}
                    >
                      <td className="email-from" title={email.from}>
                        {formatFrom(email.from)}
                      </td>
                      <td className="email-subject">{email.subject}</td>
                      <td className="email-date">{formatDate(email.date)}</td>
                      <td className="email-actions">
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteEmail(email.id); }}
                          className="email-delete-btn"
                          title="Delete"
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {emailContent && (
            <div className="email-viewer-panel">
              <div className="email-viewer-header">
                <button
                  onClick={() => { setSelectedEmail(null); setEmailContent(null); }}
                  className="email-close-btn"
                >
                  &times;
                </button>
                <h2>{emailContent.subject}</h2>
                <div className="email-meta">
                  <p><strong>From:</strong> {emailContent.from}</p>
                  <p><strong>To:</strong> {emailContent.to}</p>
                  <p><strong>Date:</strong> {formatDate(emailContent.date)}</p>
                </div>
              </div>
              <div className="email-viewer-body">
                {emailContent.html ? (
                  <iframe
                    srcDoc={emailContent.html}
                    title="Email content"
                    className="email-html-frame"
                    sandbox=""
                  />
                ) : (
                  <pre className="email-text-body">{emailContent.body}</pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default EmailPage;
